import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { Backdrop, SlideChip, SlideTitle, Card3D, Punchline, glow } from "./shared";

const TOTAL = 18;

const fadeOut = (frame: number, durationInFrames: number) =>
  interpolate(frame, [durationInFrames - 24, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

const spAppear = (frame: number, fps: number, delay = 0) =>
  interpolate(spring({ frame: frame - delay, fps, config: SPRING.snappy }), [0, 1], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

/* ──────────────── Scene 01 — Cold Open ──────────────── */
export const S01Cold: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);
  const subAp = spAppear(frame, fps, 60);
  const shardAp = spAppear(frame, fps, 20);

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonRed} />

      {/* Broken lock shards — 3D scattered */}
      {[...Array(12)].map((_, i) => {
        const angle = (i / 12) * Math.PI * 2;
        const r = 340 + (i % 3) * 80;
        const x = 960 + Math.cos(angle) * r;
        const y = 540 + Math.sin(angle) * r * 0.5;
        const rotZ = (i * 37) % 360 + frame * 0.3;
        const rotY = Math.sin(frame * 0.02 + i) * 20;
        return (
          <div key={i} style={{
            position: "absolute",
            left: x - 34, top: y - 34,
            width: 68, height: 68,
            opacity: shardAp * 0.7,
            transform: `perspective(1000px) rotateZ(${rotZ}deg) rotateY(${rotY}deg)`,
            fontSize: 54,
            filter: `drop-shadow(0 0 20px ${COLORS.neonRed}cc)`,
          }}>🔓</div>
        );
      })}

      {/* Pulsing red center glow */}
      <div style={{
        position: "absolute", left: "50%", top: "50%",
        width: 900, height: 900,
        transform: "translate(-50%, -50%)",
        borderRadius: "50%",
        background: `radial-gradient(circle, ${COLORS.neonRed}${Math.round((0.1 + Math.sin(frame * 0.05) * 0.05) * 255).toString(16).padStart(2, "0")} 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />

      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          fontSize: 96,
          fontFamily: FONTS.heading,
          fontWeight: 900,
          color: COLORS.textPrimary,
          textShadow: `0 0 40px ${COLORS.neonRed}ee, 0 0 80px ${COLORS.neonRed}77`,
          letterSpacing: -2,
          textAlign: "center",
          lineHeight: 1.05,
          opacity: ap,
          transform: `scale(${0.9 + ap * 0.1})`,
        }}>
          Your data is leaking.<br />
          <span style={{ color: COLORS.neonRed }}>Right now.</span>
        </div>
        <div style={{
          fontSize: 32,
          fontFamily: FONTS.body,
          color: COLORS.textSecondary,
          marginTop: 28,
          opacity: subAp,
          fontWeight: 500,
          letterSpacing: 1,
        }}>
          Every SaaS AI tool your team uses is a new exfiltration path.
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 02 — Title ──────────────── */
export const S02Title: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const titleSp = spring({ frame, fps, config: SPRING.bouncy });
  const titleOp = interpolate(titleSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const titleScale = interpolate(titleSp, [0, 1], [0.5, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const subOp = spAppear(frame, fps, 30);
  const shieldRot = frame * 0.8;

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonCyan} />

      {/* Rotating 3D shield ring */}
      <div style={{ position: "absolute", top: "50%", left: "50%", width: 0, height: 0, perspective: 1400 }}>
        <div style={{ transform: `rotateX(${Math.sin(frame * 0.02) * 4}deg)` }}>
          {[...Array(8)].map((_, i) => {
            const ang = (shieldRot + i * 45) * Math.PI / 180;
            const rx = 520, ry = 140;
            const x = Math.cos(ang) * rx;
            const y = Math.sin(ang) * ry;
            const inFront = Math.sin(ang) > 0;
            const depth = inFront ? 1 : 0.6;
            const colors = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonOrange, COLORS.neonPurple, COLORS.neonCyan, COLORS.neonYellow, COLORS.neonLime];
            const c = colors[i];
            return (
              <div key={i} style={{
                position: "absolute",
                left: x - 26, top: y - 26,
                fontSize: 52,
                transform: `scale(${depth})`,
                filter: `drop-shadow(0 0 20px ${c}ee)`,
                zIndex: inFront ? 10 : 1,
                opacity: titleOp,
              }}>🛡️</div>
            );
          })}
        </div>
      </div>

      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        zIndex: 5,
      }}>
        <div style={{
          fontSize: 148,
          fontFamily: FONTS.heading,
          fontWeight: 900,
          color: COLORS.textPrimary,
          textShadow: `0 0 50px ${COLORS.neonCyan}dd, 0 0 100px ${COLORS.neonCyan}55`,
          letterSpacing: -4,
          lineHeight: 1,
          transform: `scale(${titleScale})`,
          opacity: titleOp,
          textAlign: "center",
        }}>
          Security <span style={{ color: COLORS.neonCyan }}>First.</span>
        </div>
        <div style={{
          fontSize: 36,
          fontFamily: FONTS.body,
          color: COLORS.textSecondary,
          marginTop: 28,
          opacity: subOp,
          fontWeight: 500,
          letterSpacing: 2,
        }}>
          Why EAKC runs on your infrastructure — by design.
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 03 — Three Threats ──────────────── */
export const S03Threats: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  const THREATS = [
    { icon: "🩸", title: "Tenant bleed-through",  detail: "One client sees another client's data",  c: COLORS.neonRed },
    { icon: "🌐", title: "Web exfiltration",      detail: "Your KB leaks into public LLM providers", c: COLORS.neonOrange },
    { icon: "🔑", title: "Credential sprawl",     detail: "API keys multiply, none revocable",       c: COLORS.neonPink },
  ];

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonRed} dark />
      <SlideChip n={3} total={TOTAL} color={COLORS.neonRed} appear={ap} />
      <SlideTitle text="3 threats your stack already has" color={COLORS.neonRed} appear={ap} />

      <div style={{
        position: "absolute", top: 320, left: 0, right: 0,
        display: "flex", justifyContent: "center", gap: 50,
        perspective: 1600,
      }}>
        {THREATS.map((t, i) => (
          <Card3D key={i} inline w={460} h={440} color={t.c} frame={frame} delay={20 + i * 18} fps={fps} rotY={(i - 1) * 6}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 22 }}>
              <div style={{ fontSize: 140 }}>{t.icon}</div>
              <div style={{ fontSize: 34, fontFamily: FONTS.heading, fontWeight: 900, color: t.c, textAlign: "center", textShadow: glow(t.c, true), letterSpacing: -0.5, lineHeight: 1.15 }}>
                {t.title}
              </div>
              <div style={{ fontSize: 20, fontFamily: FONTS.body, color: COLORS.textSecondary, textAlign: "center", lineHeight: 1.3, maxWidth: 360 }}>
                {t.detail}
              </div>
            </div>
          </Card3D>
        ))}
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 04 — Threat 1: Tenant Bleed-through ──────────────── */
export const S04Threat1: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);
  const arrowAp = spAppear(frame, fps, 80);
  const blockAp = spAppear(frame, fps, 200);

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonRed} />
      <SlideChip n={4} total={TOTAL} color={COLORS.neonRed} appear={ap} />
      <SlideTitle text="Threat 1 · Tenant bleed-through" subtitle="When app-layer isolation fails, everyone sees everything." color={COLORS.neonRed} appear={ap} />

      {/* Two tenant DBs with bleeding arrow */}
      <div style={{ position: "absolute", top: 340, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 320 }}>
        {[
          { label: "Tenant A · Acme Corp", c: COLORS.neonBlue, docs: ["Acme Contracts", "Acme Salaries", "Acme API keys"] },
          { label: "Tenant B · Globex",    c: COLORS.neonGreen, docs: ["Globex Pricing", "Globex Roadmap", "Globex Clients"] },
        ].map((t, i) => {
          const sp = spring({ frame: frame - 20 - i * 20, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const sc = interpolate(sp, [0, 1], [0.5, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <div key={i} style={{
              width: 440,
              opacity: op,
              transform: `scale(${sc}) perspective(1200px) rotateY(${(i === 0 ? 1 : -1) * 8}deg)`,
            }}>
              <div style={{
                padding: 30,
                background: `linear-gradient(140deg, ${t.c}22, ${COLORS.bgCard}ee)`,
                border: `3px solid ${t.c}`,
                borderRadius: 22,
                boxShadow: `0 0 40px ${t.c}66, 0 20px 40px rgba(0,0,0,0.5)`,
              }}>
                <div style={{ fontSize: 60, textAlign: "center" }}>🗄️</div>
                <div style={{ fontSize: 24, fontFamily: FONTS.heading, fontWeight: 800, color: t.c, textAlign: "center", marginTop: 12, textShadow: glow(t.c) }}>
                  {t.label}
                </div>
                <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
                  {t.docs.map((d, j) => (
                    <div key={j} style={{
                      fontSize: 16,
                      fontFamily: FONTS.mono,
                      color: COLORS.textSecondary,
                      padding: "6px 14px",
                      background: `${t.c}0a`,
                      borderRadius: 8,
                      border: `1px solid ${t.c}33`,
                    }}>{d}</div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bleeding arrow A → B */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        {(() => {
          const dashOff = -(frame * 6) % 24;
          return (
            <g opacity={arrowAp * 0.9}>
              <path
                d="M 720 540 C 860 440, 1060 440, 1200 540"
                fill="none"
                stroke={COLORS.neonRed}
                strokeWidth={6}
                strokeDasharray="14 10"
                strokeDashoffset={dashOff}
                style={{ filter: `drop-shadow(0 0 10px ${COLORS.neonRed})` }}
              />
              <polygon points="1190,530 1220,540 1190,555" fill={COLORS.neonRed} style={{ filter: `drop-shadow(0 0 10px ${COLORS.neonRed})` }} />
              <text x={960} y={430} textAnchor="middle" fontSize={28} fontFamily={FONTS.mono} fill={COLORS.neonRed} fontWeight={800} style={{ filter: `drop-shadow(0 0 10px ${COLORS.neonRed})` }}>
                BLEED
              </text>
            </g>
          );
        })()}
      </svg>

      <Punchline
        text={<>One bug in app code = <span style={{ color: COLORS.neonRed }}>total data leak</span>.</>}
        color={COLORS.textPrimary}
        appear={blockAp}
      />
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 05 — Threat 2: Web Exfiltration ──────────────── */
export const S05Threat2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonOrange} />
      <SlideChip n={5} total={TOTAL} color={COLORS.neonOrange} appear={ap} />
      <SlideTitle text="Threat 2 · Web exfiltration" subtitle="Your KB flows to OpenAI, Anthropic, Google — invisibly." color={COLORS.neonOrange} appear={ap} />

      {/* Center KB icon → pipes to public LLM providers */}
      <div style={{ position: "absolute", top: 340, left: 160, width: 300, height: 300 }}>
        <div style={{
          width: "100%", height: "100%",
          borderRadius: 24,
          background: `linear-gradient(140deg, ${COLORS.neonBlue}22, ${COLORS.bgCard}ee)`,
          border: `3px solid ${COLORS.neonBlue}`,
          boxShadow: `0 0 40px ${COLORS.neonBlue}77`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 14,
        }}>
          <div style={{ fontSize: 120 }}>🏢</div>
          <div style={{ fontSize: 24, fontFamily: FONTS.heading, fontWeight: 800, color: COLORS.neonBlue, textShadow: glow(COLORS.neonBlue) }}>
            Your KB
          </div>
          <div style={{ fontSize: 16, fontFamily: FONTS.mono, color: COLORS.textMuted, textAlign: "center", padding: "0 20px" }}>
            Contracts · Strategy · People
          </div>
        </div>
      </div>

      {/* 3 LLM provider cards on right, arrows pointing from KB to them */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        {["1", "2", "3"].map((_, i) => {
          const y = 380 + i * 180;
          const dashOff = -(frame * 5 + i * 8) % 20;
          const sp = spring({ frame: frame - 40 - i * 15, fps, config: SPRING.gentle });
          const op = interpolate(sp, [0, 1], [0, 0.85], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <line
              key={i}
              x1={460} y1={490}
              x2={1260} y2={y + 60}
              stroke={COLORS.neonOrange}
              strokeWidth={5}
              strokeOpacity={op}
              strokeDasharray="14 10"
              strokeDashoffset={dashOff}
              style={{ filter: `drop-shadow(0 0 10px ${COLORS.neonOrange})` }}
            />
          );
        })}
      </svg>

      {[
        { name: "OpenAI",    sub: "gpt-4o logs", c: COLORS.neonGreen },
        { name: "Anthropic", sub: "prompt training? ❓", c: COLORS.neonOrange },
        { name: "Google",    sub: "Gemini · analytics", c: COLORS.neonRed },
      ].map((p, i) => (
        <Card3D key={p.name} x={1260} y={360 + i * 180} w={480} h={140} color={p.c} frame={frame} delay={50 + i * 15} fps={fps}>
          <div style={{ display: "flex", alignItems: "center", gap: 22, height: "100%" }}>
            <div style={{ fontSize: 60 }}>🌐</div>
            <div>
              <div style={{ fontSize: 28, fontFamily: FONTS.heading, fontWeight: 800, color: p.c, textShadow: glow(p.c) }}>{p.name}</div>
              <div style={{ fontSize: 16, fontFamily: FONTS.mono, color: COLORS.textMuted, marginTop: 4 }}>{p.sub}</div>
            </div>
          </div>
        </Card3D>
      ))}

      <Punchline
        text={<>Every query is a <span style={{ color: COLORS.neonOrange }}>training sample</span> for someone else.</>}
        color={COLORS.textPrimary}
        appear={spAppear(frame, fps, 200)}
      />
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 06 — Threat 3: Credential Sprawl ──────────────── */
export const S06Threat3: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  const KEYS = [
    { label: "OpenAI API key", c: COLORS.neonGreen, x: 180, y: 360 },
    { label: "Anthropic key",  c: COLORS.neonOrange, x: 460, y: 340 },
    { label: "Pinecone",       c: COLORS.neonPurple, x: 740, y: 380 },
    { label: "Slack bot",      c: COLORS.neonPink,   x: 180, y: 540 },
    { label: "GitHub PAT",     c: COLORS.neonBlue,   x: 460, y: 560 },
    { label: "AWS root",       c: COLORS.neonRed,    x: 740, y: 540 },
    { label: "Notion API",     c: COLORS.neonCyan,   x: 180, y: 720 },
    { label: "Figma token",    c: COLORS.neonYellow, x: 460, y: 740 },
    { label: "Zapier secret",  c: COLORS.neonLime,   x: 740, y: 720 },
  ];

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonPink} />
      <SlideChip n={6} total={TOTAL} color={COLORS.neonPink} appear={ap} />
      <SlideTitle text="Threat 3 · Credential sprawl" subtitle="No one tracks what keys exist, who owns them, or how to revoke." color={COLORS.neonPink} appear={ap} />

      {/* Scattered keys */}
      {KEYS.map((k, i) => {
        const sp = spring({ frame: frame - 20 - i * 8, fps, config: SPRING.bouncy });
        const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
        const sc = interpolate(sp, [0, 1], [0.4, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
        const rot = Math.sin(frame * 0.02 + i) * 6;
        return (
          <div key={i} style={{
            position: "absolute",
            left: k.x, top: k.y,
            width: 280, height: 110,
            opacity: op,
            transform: `scale(${sc}) rotate(${rot}deg) perspective(800px) rotateY(${Math.cos(frame * 0.015 + i) * 6}deg)`,
          }}>
            <div style={{
              width: "100%", height: "100%",
              background: `linear-gradient(135deg, ${k.c}22, ${COLORS.bgCard}ee)`,
              border: `2px solid ${k.c}`,
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "0 22px",
              boxShadow: `0 0 24px ${k.c}66`,
            }}>
              <div style={{ fontSize: 44 }}>🔑</div>
              <div>
                <div style={{ fontSize: 20, fontFamily: FONTS.heading, fontWeight: 700, color: k.c, textShadow: glow(k.c) }}>{k.label}</div>
                <div style={{ fontSize: 13, fontFamily: FONTS.mono, color: COLORS.textMuted, marginTop: 2 }}>owner: ???</div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Right: alert stats */}
      <div style={{ position: "absolute", top: 360, right: 100, width: 420, display: "flex", flexDirection: "column", gap: 24 }}>
        {[
          { k: "48+", v: "active keys in your org", c: COLORS.neonRed },
          { k: "12",  v: "never rotated",            c: COLORS.neonOrange },
          { k: "0",   v: "revocable on exit",        c: COLORS.neonPink },
        ].map((s, i) => (
          <Card3D key={i} inline w={420} h={130} color={s.c} frame={frame} delay={80 + i * 18} fps={fps}>
            <div style={{ display: "flex", alignItems: "center", gap: 22, height: "100%" }}>
              <div style={{ fontSize: 68, fontFamily: FONTS.heading, fontWeight: 900, color: s.c, textShadow: glow(s.c, true), lineHeight: 1, minWidth: 110, textAlign: "center" }}>
                {s.k}
              </div>
              <div style={{ fontSize: 20, fontFamily: FONTS.body, color: COLORS.textSecondary, fontWeight: 500 }}>
                {s.v}
              </div>
            </div>
          </Card3D>
        ))}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
